import type { PlcAddressArea } from '../types';

export type AddressSize = 'X' | 'B' | 'W' | 'D'; // Bit, Byte, Word, DWord

export interface ParsedAddress {
  readonly area: PlcAddressArea;
  readonly size: AddressSize;
  readonly byteOffset: number;
  readonly bitOffset: number; // 0-7, only valid if size === 'X'
}

// Regex to match addresses like I0.0, Q2.7, M10.3
const BIT_REGEX = /^([IQM])(\d+)\.([0-7])$/i;

// Regex to match addresses like IB0, QW2, MD4, MW10
const BYTE_WORD_DWORD_REGEX = /^([IQM])([BWD])(\d+)$/i;

// Regex to match timers and counters like T0, C5, T1.Q
const TIMER_COUNTER_REGEX = /^([TC])(\d+)(?:\.(Q|CV|ET))?$/i;

export function parseAddress(address: string): ParsedAddress | null {
  const s = address.trim().toUpperCase();

  const bitMatch = s.match(BIT_REGEX);
  if (bitMatch) {
    return {
      area: bitMatch[1] as PlcAddressArea,
      size: 'X',
      byteOffset: parseInt(bitMatch[2] ?? '0', 10),
      bitOffset: parseInt(bitMatch[3] ?? '0', 10),
    };
  }

  const bwdMatch = s.match(BYTE_WORD_DWORD_REGEX);
  if (bwdMatch) {
    return {
      area: bwdMatch[1] as PlcAddressArea,
      size: bwdMatch[2] as AddressSize,
      byteOffset: parseInt(bwdMatch[3] ?? '0', 10),
      bitOffset: 0,
    };
  }

  const tcMatch = s.match(TIMER_COUNTER_REGEX);
  if (tcMatch) {
    return {
      area: tcMatch[1] as PlcAddressArea,
      // Treat T/C as bits if they have .Q (or in general for contacts)
      size: s.endsWith('.Q') ? 'X' : 'W',
      byteOffset: parseInt(tcMatch[2] ?? '0', 10),
      bitOffset: 0,
    };
  }

  return null;
}

export function formatAddress(parsed: ParsedAddress): string {
  if (parsed.area === 'T' || parsed.area === 'C') {
    return parsed.size === 'X' ? `${parsed.area}${parsed.byteOffset}.Q` : `${parsed.area}${parsed.byteOffset}`;
  }

  if (parsed.size === 'X') {
    return `${parsed.area}${parsed.byteOffset}.${parsed.bitOffset}`;
  }

  return `${parsed.area}${parsed.size}${parsed.byteOffset}`;
}

import type { Operand } from '../runtime/ir';

export function parseOperand(valueStr: string): Operand | null {
  const s = valueStr.trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    return { type: 'CONSTANT', value: Number(s) };
  }
  const addr = parseAddress(s);
  if (addr) {
    return { type: 'ADDRESS', addr, raw: s };
  }
  return null;
}
